import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const JOB_LABELS = {
  liyana_mahatha: 'Liyana Mahatha',
  nattami: 'Nattami',
  pariganaka: 'Pariganaka',
  wenath: 'Wenath',
};

const CATEGORY_LABELS = {
  vegetables: 'Vegetables',
  fruits: 'Fruits',
  potato_onion: 'Potato & Onion (Import)',
  other: 'Other',
};

export default function Reports() {
  const [activeReport, setActiveReport] = useState('blocks');
  const [blockData, setBlockData] = useState([]);
  const [itemData, setItemData] = useState([]);
  const [staffSummary, setStaffSummary] = useState([]);
  const [staffDetails, setStaffDetails] = useState([]);
  const [selectedJobType, setSelectedJobType] = useState('');
  const [loading, setLoading] = useState(false);

  // Load block report
  useEffect(() => {
    if (activeReport !== 'blocks') return;
    setLoading(true);
    api.get('/reports/blocks')
      .then(r => setBlockData(r.data))
      .catch(() => toast.error('Failed to load block report'))
      .finally(() => setLoading(false));
  }, [activeReport]);

  // Load selling items report
  useEffect(() => {
    if (activeReport !== 'items') return;
    setLoading(true);
    api.get('/reports/selling-items')
      .then(r => setItemData(r.data))
      .catch(() => toast.error('Failed to load selling items report'))
      .finally(() => setLoading(false));
  }, [activeReport]);

  // Load staff summary report
  useEffect(() => {
    if (activeReport !== 'staff-summary') return;
    setLoading(true);
    api.get('/reports/staff-summary')
      .then(r => setStaffSummary(r.data))
      .catch(() => toast.error('Failed to load staff summary'))
      .finally(() => setLoading(false));
  }, [activeReport]);

  // Load staff details report
  useEffect(() => {
    if (activeReport !== 'staff-details') return;
    setLoading(true);
    const url = selectedJobType
      ? `/reports/staff-details?job_type=${encodeURIComponent(selectedJobType)}`
      : '/reports/staff-details';
    api.get(url)
      .then(r => setStaffDetails(r.data))
      .catch(() => toast.error('Failed to load staff details'))
      .finally(() => setLoading(false));
  }, [activeReport, selectedJobType]);

  const downloadCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to download');
      return;
    }

    const headers = Object.keys(data[0]);
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => {
        const val = row[h];
        if (val === null || val === undefined) return '';
        if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
        return val;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '28px 32px', height: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Reports</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className={activeReport === 'blocks' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveReport('blocks')}
          >
            Block Report
          </button>
          <button
            className={activeReport === 'items' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveReport('items')}
          >
            Selling Items
          </button>
          <button
            className={activeReport === 'staff-summary' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveReport('staff-summary')}
          >
            Staff Summary
          </button>
          <button
            className={activeReport === 'staff-details' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveReport('staff-details')}
          >
            Staff Details
          </button>
        </div>
      </div>

      {/* Block Report */}
      {activeReport === 'blocks' && (
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid var(--surface2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Block Summary</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => downloadCSV(blockData, 'block-report')}>
              ↓ Download CSV
            </button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>Loading…</p>
            ) : blockData.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>No data available</p>
            ) : (
              <table style={{ width: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Block</th>
                    <th>Total Shops</th>
                    <th>Filled</th>
                    <th>Staff Count</th>
                  </tr>
                </thead>
                <tbody>
                  {blockData.map(row => (
                    <tr key={row.block}>
                      <td style={{ fontWeight: 600 }}>Block {row.block}</td>
                      <td style={{ textAlign: 'center' }}>{row.shop_count}</td>
                      <td style={{ textAlign: 'center', color: '#16a34a', fontWeight: 600 }}>{row.filled_count}</td>
                      <td style={{ textAlign: 'center' }}>{row.staff_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Selling Items Report */}
      {activeReport === 'items' && (
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid var(--surface2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Selling Items by Category</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => downloadCSV(itemData, 'selling-items-report')}>
              ↓ Download CSV
            </button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>Loading…</p>
            ) : itemData.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>No data available</p>
            ) : (
              <table style={{ width: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Category</th>
                    <th>Shops Selling</th>
                    <th>Total Items</th>
                  </tr>
                </thead>
                <tbody>
                  {itemData.map(row => (
                    <tr key={row.category}>
                      <td><span className="badge badge-blue">{CATEGORY_LABELS[row.category] || row.category}</span></td>
                      <td style={{ textAlign: 'center' }}>{row.shop_count}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.item_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Staff Summary Report */}
      {activeReport === 'staff-summary' && (
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid var(--surface2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Staff by Designation</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => downloadCSV(staffSummary, 'staff-summary-report')}>
              ↓ Download CSV
            </button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>Loading…</p>
            ) : staffSummary.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>No data available</p>
            ) : (
              <table style={{ width: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Designation</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {staffSummary.map(row => (
                    <tr key={row.job_type} style={{ cursor: 'pointer' }} onClick={() => { setActiveReport('staff-details'); setSelectedJobType(row.job_type); }}>
                      <td style={{ color: '#1d4ed8', textDecoration: 'underline' }}>{row.job_type_label}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Staff Details Report */}
      {activeReport === 'staff-details' && (
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid var(--surface2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>Staff Details</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => downloadCSV(staffDetails, 'staff-details-report')}>
                ↓ Download CSV
              </button>
            </div>
            <select className="form-select" style={{ maxWidth: 250 }} value={selectedJobType} onChange={e => setSelectedJobType(e.target.value)}>
              <option value="">All Designations</option>
              {Object.entries(JOB_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>Loading…</p>
            ) : staffDetails.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>No staff found</p>
            ) : (
              <table style={{ width: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Shop</th>
                    <th>Market Name</th>
                    <th>Phone</th>
                    <th>ID No</th>
                  </tr>
                </thead>
                <tbody>
                  {staffDetails.map(row => (
                    <tr key={row.id}>
                      <td style={{ fontWeight: 500 }}>{row.name}</td>
                      <td><span className="badge badge-blue">{row.job_type_label}</span></td>
                      <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, fontWeight: 600 }}>{row.shop_code}</td>
                      <td style={{ fontSize: 12 }}>{row.market_name || '—'}</td>
                      <td style={{ fontSize: 12 }}>{row.phone || '—'}</td>
                      <td style={{ fontSize: 12, fontFamily: 'DM Mono, monospace' }}>{row.id_no || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
